// What happened to each onboarding panel. The onboarding asks two things, each on a panel of its
// own: the diet, and the categories and regions. For each one we keep when it was shown, skipped and
// completed, so that when the onboarding comes round again it only shows what is still outstanding,
// and so there is data to decide what to show in future if more panels are added.
//
// Stored on the account as one JSON string (`onboarding_panels`), and also sent as analytics events.

export const PANEL = { DIET: 'diet', INTERESTS: 'interests' };
export const PANEL_IDS = [PANEL.DIET, PANEL.INTERESTS];

export const PANEL_EVENT = { SHOWN: 'shown', SKIPPED: 'skipped', COMPLETED: 'completed' };

// Matches the limit the server accepts.
const MAX_TIMES = 10;

const asArray = value => (Array.isArray(value) ? value : []);

const emptyPanel = () => ({
  [PANEL_EVENT.SHOWN]: [],
  [PANEL_EVENT.SKIPPED]: [],
  [PANEL_EVENT.COMPLETED]: [],
});

const emptyPanels = () => PANEL_IDS.reduce((all, id) => ({ ...all, [id]: emptyPanel() }), {});

/**
 * The record of what happened to each panel, always in full shape. Anything missing or malformed
 * counts as "nothing happened".
 */
export const parsePanels = settings => {
  let raw = settings && settings.onboarding_panels;
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw);
    } catch (error) {
      raw = null;
    }
  }
  const panels = emptyPanels();
  if (!raw || typeof raw !== 'object') {
    return panels;
  }
  PANEL_IDS.forEach(id => {
    Object.values(PANEL_EVENT).forEach(event => {
      panels[id][event] = asArray(raw[id] && raw[id][event]).filter(
        time => typeof time === 'string' && !Number.isNaN(new Date(time).getTime()),
      );
    });
  });
  return panels;
};

const panelHasRecord = panel => Object.values(panel).some(times => times.length > 0);

export const hasPanelRecord = panels => PANEL_IDS.some(id => panelHasRecord(panels[id]));

export const serializePanels = panels => JSON.stringify(panels);

/** The record with one more event on one panel. */
export const recordPanelEvent = (panels, panelId, event, now = new Date()) => {
  const next = parsePanels({ onboarding_panels: panels });
  next[panelId][event] = [...next[panelId][event], now.toISOString()].slice(-MAX_TIMES);
  return next;
};

// Whether the account already holds an answer for a panel, from before panels were recorded.
const savedAnswer = (settings, panelId) =>
  panelId === PANEL.DIET
    ? asArray(settings && settings.nutrition_diet_prefer).length > 0
    : asArray(settings && settings.culinary_topics).length > 0 ||
      asArray(settings && settings.culinary_regions).length > 0;

/**
 * Whether the panel was completed. Anyone who went through the onboarding before panels were
 * recorded and finished it ("completed") has no record for the panel, so what they saved stands in
 * for it. A skipped or never-seen panel stays outstanding, whatever else is saved on the account.
 */
export const isPanelCompleted = (settings, panelId) => {
  const panel = parsePanels(settings)[panelId];
  if (panel[PANEL_EVENT.COMPLETED].length > 0) {
    return true;
  }
  return (
    !panelHasRecord(panel) &&
    !!settings &&
    settings.onboarding_preferences_status === 'completed' &&
    savedAnswer(settings, panelId)
  );
};

/** The panels still to be shown, in the order they are asked. */
export const pendingPanels = settings => PANEL_IDS.filter(id => !isPanelCompleted(settings, id));

/** The record for an account that was asked on these dates and skipped everything. */
export const skippedOn = dates =>
  PANEL_IDS.reduce(
    (all, id) => ({
      ...all,
      [id]: { ...emptyPanel(), [PANEL_EVENT.SHOWN]: dates, [PANEL_EVENT.SKIPPED]: dates },
    }),
    {},
  );
